<template>
    <page-layout :show-nav="shouldShowNav">
        <g-loader v-if="isLoading" type="fixed" />
        <div
            v-else-if="isProposalCompleted && !isProposalAccepted"
            :class="{ 'proposal-confirmation--nav': shouldShowNav }"
        >
            <proposal-confirmation :campaign="campaign" :contact="contact" />
        </div>
        <template v-else>
            <template slot="header">
                <title-bar :title="brandName" class="brand-name" />
            </template>
            <template slot="default">
                <div class="step-card-wrapper">
                    <g-typography tag="h1" font-weight="semibold">
                        {{ campaignTitle }}
                    </g-typography>
                </div>
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <template v-if="campaignObjective">
                    <div class="margin-bottom-small">
                        <g-typography tag="h4" value="Overview" font-weight="semibold" />
                    </div>
                    <div class="margin-bottom-normal">
                        <g-truncate>
                            <div v-html="$sanitize(campaignObjective)" />
                        </g-truncate>
                    </div>
                </template>
                <div
                    v-if="
                        isProposalCompleted &&
                        (tags.length > 0 ||
                            (campaign.show_revenue_tracking_information && (affiliateLink || discountCodes.length > 0)))
                    "
                    class="margin-bottom-normal"
                >
                    <g-typography tag="p" font-weight="semibold" margins>Important Information</g-typography>
                    <tags-box :tags="tags" :custom-description="tagsDescription" class="margin-bottom-small" />
                    <template v-if="campaign.show_revenue_tracking_information">
                        <div v-if="affiliateLink" class="margin-bottom-small">
                            <link-code-box type="link" :text="affiliateLink" />
                        </div>
                        <div v-for="discountCode in discountCodes" class="margin-bottom-small">
                            <link-code-box type="code" :text="discountCode" />
                        </div>
                    </template>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_COMPENSATION)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('compensation')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_COMPENSATION)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template slot="subTitle">
                            {{ compensationString }}
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_PRODUCTS)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('products')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_PRODUCTS)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template v-if="selectProducts" slot="subTitle">
                            {{ selectedProductsString }}
                        </template>
                        <template slot="completedDetails">
                            <selected-product-card
                                v-for="product in products"
                                :key="product.id"
                                :product="product"
                                :campaign="campaign"
                            />
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_TASKS)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('tasks')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_TASKS)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                        :proposal="proposal"
                    >
                        <template v-if="!isProposalAccepted" slot="subTitle">
                            {{ requiredTasks().length }} Required, {{ optionalTasksCount }} Optional
                        </template>
                        <template slot="completedDetails">
                            <template v-for="task in tasks">
                                <campaign-task :task="task" class="margin-bottom-small" />
                            </template>
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="isProposalAccepted" class="step-card-wrapper">
                    <proposal-files-card
                        :upload-url="proposalFileProps.fileUploadUrl"
                        :upload-headers="proposalFileProps.fileUploadHeaders"
                        :files="proposalFileProps.proposalFiles"
                        :isFileUploading="isFileUploading"
                        @handle-file-upload="handleFileUpload"
                        @handle-file-delete="handleFileDelete"
                    />
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_NETWORKS)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('social-networks')"
                        :step="getStep(STEP_NAMES.NAME_NETWORKS)"
                        :current-step-name="currentStepName"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template slot="subTitle" slot-scope="{ isComplete }">
                            <span v-if="isComplete"> {{ connectedNetworks.length }} connected</span>
                            <div v-else>
                                <span v-for="network in campaignNetworks">
                                    <network-icon :network="network.slug" />
                                </span>
                            </div>
                        </template>
                        <template slot="completedDetails">
                            <g-media-object
                                v-for="contactNetwork in contactNetworks"
                                :key="contactNetwork.id"
                                class="margin-bottom-small"
                            >
                                <template slot="left">
                                    <network-icon :network="contactNetwork.network.slug" />
                                </template>
                                <template>
                                    <div class="profile-info">
                                        <div class="margin-bottom-tiny">
                                            <g-typography tag="h4" :value="networkName(contactNetwork)" />
                                        </div>
                                        <div>
                                            <g-typography
                                                tag="h5"
                                                color="secondary"
                                                :value="contactNetwork.network_name || contactNetwork.network_username"
                                            />
                                        </div>
                                    </div>
                                </template>
                            </g-media-object>
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_SHIPPING_INFO)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('shipping-info')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_SHIPPING_INFO)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template v-if="address && isComplete" slot="subTitle" slot-scope="{ isComplete }">
                            {{ address.first_name }} {{ address.last_name }}<br />
                            {{ address.street }} {{ address.street_2 }}<br />
                            {{ address.city }}, {{ address.state_code ? address.state_code : address.state }}
                            {{ address.zip }}
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_PERSONAL_INFO)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('personal-info')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_PERSONAL_INFO)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template v-if="isComplete" slot="subTitle" slot-scope="{ isComplete }">
                            <template v-if="hasPhoneField && !!contact.phone_number_display">
                                Phone: {{ contactPhoneNumber }}<br />
                            </template>
                            <template v-if="hasGenderField && !!contact.gender">
                                Gender: {{ contact.gender }}<br />
                            </template>
                            <template v-for="field in campaignFields">
                                <span v-if="!!field.value" class="campaign-field"
                                    >{{ getCustomFieldLabel(field) }}: {{ getFormattedFieldValue(field) }}</span
                                >
                            </template>
                        </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_CAMPAIGN_ASSETS)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('brand-assets')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_CAMPAIGN_ASSETS)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    >
                        <template slot="subTitle"> {{ campaignAssets.length }} assets available </template>
                    </generic-step-card>
                </div>
                <div v-if="showStep(STEP_NAMES.NAME_CONTENT_AGREEMENT)" class="step-card-wrapper">
                    <generic-step-card
                        :title="stepTitle('content-rights')"
                        :current-step-name="currentStepName"
                        :step="getStep(STEP_NAMES.NAME_CONTENT_AGREEMENT)"
                        :is-preview="isProposalPreview"
                        :proposal-accepted="isProposalAccepted"
                    />
                </div>
            </template>
            <template v-if="!isProposalAccepted" slot="footer">
                <div class="g-px-6">
                    <g-button
                        :loading="isCompletingProposal"
                        class="g-p-0"
                        shape="round"
                        theme="live"
                        type="primary"
                        block
                        :disabled="!isComplete"
                        size="xlarge"
                        @click="$emit('complete-proposal')"
                    >
                        Submit Proposal
                    </g-button>
                    <div class="powered-by-grin">
                        <powered-by-grin :clickable="true" />
                    </div>
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import * as _ from 'lodash';
    import TitleBar from '../../library/TitleBar';
    import PageLayout from '../../layouts/PageLayout';
    import GenericStepCard from './partials/GenericStepCard';
    import CampaignTask from '../campaigns/partials/CampaignTask';
    import SelectedProductCard from './partials/SelectedProductOverviewCard';
    import COMPENSATION_NAMES from '../../../constants/compensation-names';
    import * as STEP_NAMES from '../../../constants/proposal-steps';
    import TasksMixin from './mixins/TasksMixin';
    import PersonalInfoMixin from './mixins/PersonalInfoMixin';
    import ProductsMixin from './mixins/ProductsMixin';
    import ProposalMixin from './mixins/ProposalMixin';
    import STEP_CUSTOMIZATION_KEYS from '../../../constants/proposal-step-customization-keys';
    import { getCustomizableTitle, getStaticTitle } from '../../../helpers/default-texts';
    import { formatUsPhoneNumber } from '../../../helpers/formatters';
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import ProposalConfirmation from './partials/ProposalConfirmation';
    import ProposalFilesCard from './partials/ProposalFilesCard';
    import TagsBox from './partials/TagsBox';
    import LinkCodeBox from './partials/LinkCodeBox';
    import { INSTAGRAM_ID, TIKTOK_ID, PINTEREST_ID } from '../../../constants/networks';
    import NetworkIcon from '../../../../src/components/interface/elements/NetworkIcon.vue';

    export default {
        name: 'ProposalOverviewView',
        components: {
            TagsBox,
            ProposalFilesCard,
            ProposalConfirmation,
            ErrorMessageCard,
            PageLayout,
            TitleBar,
            GenericStepCard,
            SelectedProductCard,
            CampaignTask,
            LinkCodeBox,
            NetworkIcon
        },
        mixins: [TasksMixin, ProductsMixin, ProposalMixin, PersonalInfoMixin],
        data() {
            return {
                STEP_NAMES
            };
        },
        props: {
            campaign: {
                type: Object
            },
            contact: {
                type: Object
            },
            proposal: {
                type: Object
            },
            campaignFields: {
                type: Array
            },
            proposalSteps: {
                type: Object
            },
            currentProposalStep: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isProposalPreview: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: null
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            proposalFileProps: {
                type: Object
            },
            affiliateLink: {
                type: String
            },
            discountCodes: {
                type: Array,
                default: []
            },
            isFileUploading: {
                type: Boolean
            }
        },
        computed: {
            shouldShowNav() {
                return !!this.isProposalCompleted;
            },
            brandName() {
                return _.get(this, 'campaign.brand.name', '');
            },
            campaignTitle() {
                const influencerTitle = _.get(this, 'campaign.influencer_title', null);
                return influencerTitle ? influencerTitle : _.get(this, 'campaign.title', '');
            },
            campaignObjective() {
                return _.get(this, 'campaign.objective');
            },
            proposalNetworks() {
                return _.uniqBy(
                    this.proposal.tasks.filter((task) => task.network_id).map((task) => task.network),
                    'id'
                );
            },
            campaignNetworks() {
                return _.uniqBy(
                    _.get(this, 'campaign.tasks', [])
                        .filter((task) => task.network_id)
                        .map((task) => task.network),
                    'id'
                );
            },
            contactNetworks() {
                return this.contact.networks
                    .filter((contactNetwork) => contactNetwork.network_id)
                    .filter((contactNetwork) => this.hasAuth(contactNetwork));
            },
            connectedNetworks() {
                return _.uniqBy(
                    this.contactNetworks.map((contactNetwork) => contactNetwork.network),
                    'id'
                );
            },
            currentStepName() {
                return _.get(this, 'currentProposalStep.name', null);
            },
            address() {
                return this.contact.primary_address;
            },
            campaignAssets() {
                return _.get(this, 'campaign.assets', []);
            },
            compensationString() {
                return _.get(this, 'campaign.compensation', [])
                    .map((type) => COMPENSATION_NAMES[type])
                    .join(', ');
            },
            products() {
                return _.get(this.$root, 'v2.loadedProducts', []);
            },
            tasks() {
                return this.isProposalAccepted || this.isTasksStepCompleted
                    ? this.proposalTasks
                    : _.get(this, 'campaign.tasks', []);
            },
            isTasksStepCompleted() {
                return !!_.findKey(
                    this.proposalSteps,
                    (step) => step.name === STEP_NAMES.NAME_TASKS && step.is_complete === true
                );
            },
            optionalTasksCount() {
                return this.getStep(STEP_NAMES.NAME_TASKS).is_started
                    ? this.addedTasks.length
                    : this.optionalTasks().length;
            },
            contactPhoneNumber() {
                if (this.contact.phone_country_code.toUpperCase() === 'US') {
                    return formatUsPhoneNumber(this.contact.phone_number_display);
                }

                return this.contact.phone_number_display;
            },
            isComplete() {
                if (!this.proposalSteps) {
                    return false;
                }

                return !_.findKey(this.proposalSteps, (step) => step.is_complete === false);
            }
        },
        methods: {
            getStep(stepName) {
                return this.proposalSteps[stepName];
            },
            showStep(stepName) {
                return !!this.proposalSteps[stepName];
            },
            stepTitle(stepAlias) {
                if (STEP_CUSTOMIZATION_KEYS.titles.hasOwnProperty(stepAlias)) {
                    return getCustomizableTitle(this.campaign, STEP_CUSTOMIZATION_KEYS.titles[stepAlias]);
                }

                return getStaticTitle(stepAlias);
            },
            networkName(contactNetwork) {
                if (contactNetwork.network_id === INSTAGRAM_ID && contactNetwork.is_business) {
                    return `${contactNetwork.network.name} (Creator/Business)`;
                }
                return contactNetwork.network.name;
            },
            getCustomFieldLabel(field) {
                return field.external_label ? field.external_label : field.label;
            },
            handleFileUpload(info) {
                this.$emit('proposal-file-upload', info);
            },
            handleFileDelete(data) {
                this.$emit('proposal-file-delete', data);
            },
            hasAuth(contactNetwork) {
                // TikTok and Pinterest do not have auth, so a soft connection is accepted
                if (contactNetwork.network_id === TIKTOK_ID || contactNetwork.network_id === PINTEREST_ID) {
                    return true;
                }

                // Otherwise return true if the network connection type is not soft
                return contactNetwork.network_connection_type && contactNetwork.network_connection_type !== 'soft';
            }
        }
    };
</script>

<style lang="scss" scoped>
    .step-card-wrapper {
        margin-bottom: $g-spacing-5;
    }

    .no-margins {
        & ::v-deep p {
            margin: 0;
        }
    }

    .margin-bottom-tiny {
        margin-bottom: $g-spacing-1;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .proposal-confirmation--nav {
        padding-top: $g-spacing-5;
    }

    .profile-info {
        padding-left: $g-spacing-3;
    }

    .campaign-field {
        display: block;
        white-space: pre;
    }

    .powered-by-grin {
        text-align: center;
        margin-top: $g-spacing-4;
    }
    .gi-tiktok {
        width: 1.25em;
        display: inline-block;
        text-align: center;
        margin-right: 2px !important;
        height: 1.5rem;
        margin-left: 3px;
    }
</style>
